import {
  Fragment,
  lazy,
  type ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent } from '@/components/ui/sheet';

const ChatThreadList = lazy(() =>
  import('@/components/chat/ChatThreadList').then((m) => ({ default: m.ChatThreadList })),
);

import { IngestHealthBanner } from '@/components/admin/IngestHealthBanner';
import { StorageHealthBanner } from '@/components/admin/StorageHealthBanner';
import { ChatMentionsInbox } from '@/components/chat/ChatMentionsInbox';
import { useCodeReviewEnabled } from '@/components/codeReview/useCodeReviewEnabled';
import { DownloadAppButton } from '@/components/DownloadAppButton';
import { GlobalTaskControls } from '@/components/tasks/GlobalTaskControls';
import { NotificationBell } from '@/components/tasks/NotificationBell';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { api, apiPublic } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { subscribe as subscribeDashboardEvent } from '@/lib/dashboardEvents';
import {
  BookOpen,
  Bug,
  ChartLine,
  ClipboardCheck,
  ClipboardList,
  FlaskConical,
  Gauge,
  GitPullRequest,
  Inbox,
  Lightbulb,
  ListChecks,
  ListTree,
  LogOut,
  type LucideIcon,
  MessageCircle,
  NotebookPen,
  Package,
  ScrollText,
  Server,
  Settings,
  Shield,
  SlidersHorizontal,
  Tag,
  Target,
  TrendingUp,
  Users,
  Wrench,
} from '@/lib/icons';
import { DASHBOARD_PATHS, PATHS } from '@/lib/paths';
import { startPresence } from '@/lib/presence';
import { getTriageBadgeCount } from '@/lib/tasksApi';
import { ProjectFilter, useProjectFilter } from '@/lib/useProjectFilter';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Nav items
// ---------------------------------------------------------------------------

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  matchPaths?: string[];
  badge?: 'triage';
}

// Grouped so the sidebar reads by intent rather than a single generic
// "Navigation" bucket. Monitor = observe what players are doing on prod,
// Community = talk with them, Build = ship changes, Testing = QA before ship,
// Infrastructure = the agent/runtime layer that executes all of the above.
// Every item carries its own distinct Lucide glyph (from @/lib/icons) so the
// icon grid stays scannable — no two tiles share an icon.
const monitorNavItems: NavItem[] = [
  { label: 'Issues', path: '/issues', icon: Bug, matchPaths: ['/issues'] },
  { label: 'Sessions', path: '/logs', icon: ScrollText, matchPaths: ['/logs', '/linked-sessions'] },
  { label: 'Users', path: '/users', icon: Users, matchPaths: ['/users', '/devices'] },
];

const tasksNavItems: NavItem[] = [
  { label: 'Triage', path: '/triage', icon: Inbox, matchPaths: ['/triage'], badge: 'triage' },
  { label: 'My Tasks', path: '/tasks', icon: ClipboardCheck },
  { label: 'Tasks', path: '/tasks/list', icon: ClipboardList, matchPaths: ['/tasks/list'] },
  { label: 'People', path: '/tasks/people', icon: TrendingUp, matchPaths: ['/tasks/people'] },
  {
    label: 'QA Queue',
    path: '/qa-queue',
    icon: ListChecks,
    matchPaths: ['/qa-queue', '/testing/qa'],
  },
  // QA Goals — authored objectives testers must hit (Bugpunch.Goal call sites).
  // Distinct from Coverage below: goals are specific targets; coverage is the
  // freshness map of every UI area.
  { label: 'QA Goals', path: '/qa-goals', icon: Target, matchPaths: ['/qa-goals'] },
  // Coverage — UI test-freshness treemap of every canvas/area in a build (the
  // unreached map). Different concept from QA Goals; its own page.
  { label: 'Coverage', path: '/coverage', icon: ListTree, matchPaths: ['/coverage'] },
  { label: 'Wiki', path: '/wiki', icon: NotebookPen, matchPaths: ['/wiki'] },
];

const analyticsNavItems: NavItem[] = [
  { label: 'Analytics', path: '/analytics', icon: ChartLine, matchPaths: ['/analytics'] },
  { label: 'Performance', path: '/performance', icon: Gauge, matchPaths: ['/performance'] },
  {
    label: 'Game Config',
    path: '/game-config',
    icon: SlidersHorizontal,
    matchPaths: ['/game-config'],
  },
];

// Feedback is for everyone; QA also gets the support Inbox appended at
// runtime (see communityItems in AppLayout) — the Inbox tile is the old
// top-header button relocated here to free header space.
const communityNavItems: NavItem[] = [
  { label: 'Feedback', path: '/feedback', icon: Lightbulb, matchPaths: ['/feedback'] },
];

const buildNavItems: NavItem[] = [
  { label: 'Releases', path: '/', icon: Tag, matchPaths: ['/', '/releases'] },
  {
    label: 'Code Reviews',
    path: '/code-reviews',
    icon: GitPullRequest,
    matchPaths: ['/code-reviews'],
  },
  { label: 'Artifacts', path: '/artifacts', icon: Package, matchPaths: ['/artifacts'] },
];

const projectAdminNavItems: NavItem[] = [
  { label: 'Project Admin', path: '/project-admin', icon: Shield, matchPaths: ['/project-admin'] },
  {
    label: 'Devtools',
    path: '/tools',
    icon: Wrench,
    matchPaths: ['/tools', '/scripts', '/debug-tools', '/issues/attachment-rules'],
  },
];

const testingNavItems: NavItem[] = [
  {
    label: 'Tests',
    path: '/testing/tests',
    icon: FlaskConical,
    matchPaths: ['/testing/tests', '/testing/sessions'],
  },
  { label: 'Runners', path: '/runners', icon: Server, matchPaths: ['/runners', '/agents'] },
];

function isRouteActive(pathname: string, item: NavItem): boolean {
  if (!item.matchPaths) return pathname === item.path;
  return item.matchPaths.some((p) => {
    if (p === '/') return pathname === '/';
    return pathname.startsWith(p);
  });
}

// Renders a group's nav items as a compact icon grid with small labels —
// 3 tiles per row when expanded, a single icon-only column when the sidebar
// is collapsed to the icon rail. Replaces the old one-row-per-item list so
// the full ~28-item nav fits without scrolling. Any badge (triage / feedback
// count) sits in the tile's top-right corner.
function NavTileGrid({
  items,
  getBadge,
}: {
  items: NavItem[];
  getBadge?: (item: NavItem) => number;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SidebarGroupContent>
      <div className="grid grid-cols-3 gap-1 group-data-[collapsible=icon]:grid-cols-1">
        {items.map((item) => {
          const active = isRouteActive(location.pathname, item);
          const Icon = item.icon;
          const badge = getBadge?.(item) ?? 0;
          return (
            <button
              key={item.path}
              type="button"
              title={item.label}
              aria-current={active ? 'page' : undefined}
              onClick={() => {
                // Re-tapping the item whose own path you're already on: a
                // same-path navigate() is a no-op, so signal the active page to
                // refresh itself (e.g. Issues resets to page 1) instead. Guard
                // on the exact path — not `active` — so items that stay lit on a
                // sibling route (Users on /devices, Releases on /releases) still
                // navigate to their canonical path.
                if (location.pathname === item.path) {
                  window.dispatchEvent(new CustomEvent('bp:nav-reselect', { detail: item.path }));
                } else {
                  navigate(item.path);
                }
              }}
              className={cn(
                'relative flex h-14 flex-col items-center justify-center gap-1 rounded-md px-0.5 text-center text-sidebar-foreground/80 ring-sidebar-ring outline-hidden transition-colors [&>svg]:size-5',
                'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2',
                active && 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/30',
                'group-data-[collapsible=icon]:h-9',
              )}
            >
              <Icon />
              <span className="line-clamp-2 break-words text-[10px] leading-[1.1] group-data-[collapsible=icon]:hidden">
                {item.label}
              </span>
              {badge > 0 && (
                <span
                  aria-label={`${badge}`}
                  className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 font-mono text-[9px] tabular-nums text-primary-foreground group-data-[collapsible=icon]:hidden"
                >
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </SidebarGroupContent>
  );
}

// ---------------------------------------------------------------------------
// AppLayout
// ---------------------------------------------------------------------------

interface AppLayoutProps {
  children: ReactNode;
}

// Chats badge = total of unreadByQa across every project the caller has
// access to. Powered by the dedicated /api/chat/unread-count endpoint —
// one cheap aggregate query, no per-thread iteration on the client. Polled
// every 30s with a focus re-fetch so the pill reconciles the moment QA
// returns to the tab. Also subscribes to the dashboard bus topic `chat`
// so new SDK messages bump the badge + fire a browser notification from
// any page (not just /chats).
//
// QA role gets desktop notifications proactively: the sidebar pill can lag
// behind the WS push by a frame or two, and QA shouldn't have to detour to
// /chats just to grant permission. We prompt once per browser on app mount
// for QA users, and fire notifications from any page that isn't /chats
// itself (focus check there avoids double-notifying the active reader).
function useChatsBadge(token: string | null, isQa: boolean): number {
  const [count, setCount] = useState(0);
  // Latest-ref so the notification click handler can SPA-navigate without
  // adding `navigate` to the effect deps (which would re-subscribe the WS).
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    // No token → don't poll. The signed-out reset is derived in the
    // return-value below rather than via a synchronous setCount(0) in the
    // effect body (avoids set-state-in-effect cascade). Initial pull is
    // deferred into a microtask so its setCount lands in a callback.
    if (!token) return;
    let cancelled = false;

    // Proactive permission prompt for QA. Once per browser session — the
    // browser itself remembers the answer across reloads, but we still
    // gate on a sessionStorage flag so we don't re-prompt on a page nav
    // within the same tab if the user dismissed.
    if (
      isQa &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      !sessionStorage.getItem('chat_notify_prompted')
    ) {
      sessionStorage.setItem('chat_notify_prompted', '1');
      Notification.requestPermission().catch(() => {
        /* ignore */
      });
    }

    async function pull() {
      try {
        const data = await api<{ total: number }>(DASHBOARD_PATHS.chatUnreadCount);
        if (!cancelled) setCount(data.total ?? 0);
      } catch {
        /* ignore — next tick retries */
      }
    }

    queueMicrotask(pull);
    const handle = window.setInterval(pull, 30_000);
    const onFocus = () => pull();
    window.addEventListener('focus', onFocus);

    // Live push — every dashboard tab gets a `chat` frame on new SDK
    // messages. Bump count instantly so the pill doesn't wait up to 30s
    // for the next poll. Reconciliation still happens via pull() — if a
    // push is missed or duplicates, the next poll corrects it.
    const unsubscribe = subscribeDashboardEvent('chat', (raw) => {
      const msg = raw as {
        type?: string;
        message?: {
          sender?: string;
          body?: string;
          userName?: string | null;
          threadId?: string;
          avatarUrl?: string | null;
        };
        thread?: { playerName?: string | null; subject?: string | null };
        threadId?: string;
      };
      if (msg.type !== 'message' || !msg.message) return;
      if (msg.message.sender !== 'sdk') return;
      setCount((c) => c + 1);
      // Best-effort browser notification — fires whenever permission is
      // granted (QA gets prompted on mount; non-QA only if they granted
      // via the in-context /chats prompt). Skip only when the user is
      // already on /chats with the tab focused — that page renders the
      // message inline so a desktop pop-up would just duplicate it.
      if (typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      const onChatsPage = location.pathname.startsWith('/chats');
      if (onChatsPage && !document.hidden && document.hasFocus()) return;
      // Carry who + what, and deep-link the click into the inbox with the
      // thread open.
      const tid = msg.message.threadId ?? msg.threadId;
      const who = msg.thread?.playerName?.trim() || msg.message.userName || 'A player';
      const preview =
        msg.message.body?.trim() || msg.thread?.subject?.trim() || 'Sent an attachment';
      try {
        const n = new Notification(`${who} · new message`, {
          body: preview,
          tag: tid,
          icon: msg.message.avatarUrl || undefined,
        });
        n.onclick = () => {
          window.focus();
          if (tid) navigateRef.current(`/chats?thread=${encodeURIComponent(tid)}`);
          n.close();
        };
      } catch {
        /* permission revoked between checks */
      }
    });

    return () => {
      cancelled = true;
      window.clearInterval(handle);
      window.removeEventListener('focus', onFocus);
      unsubscribe();
    };
  }, [token, isQa]);

  return token ? count : 0;
}

// Feedback "new since last visit" badge. Tracked per-browser via
// localStorage — counts feedback items whose CreatedAt is after the last
// time this browser opened /feedback. Good enough until we add a real
// per-user "last seen" hook server-side.
function useFeedbackBadge(projectId: string | null, token: string | null): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    // Same pattern as useChatsBadge — derive the signed-out zero in the
    // return-value, defer initial pull into a microtask.
    if (!token) return;
    let cancelled = false;

    async function pull() {
      try {
        const raw = localStorage.getItem(`feedback_last_viewed_${projectId ?? '_all'}`);
        const lastViewed =
          raw && Number.isFinite(Date.parse(raw)) ? new Date(raw).toISOString() : '';
        const qs = new URLSearchParams();
        if (lastViewed) qs.set('since', lastViewed);
        const data = await api<{ count: number }>(
          `${DASHBOARD_PATHS.feedbackCount}${qs.size ? `?${qs}` : ''}`,
        );
        if (!cancelled) setCount(data.count ?? 0);
      } catch {
        /* ignore */
      }
    }

    queueMicrotask(pull);
    const handle = window.setInterval(pull, 30_000);
    const onFocus = () => pull();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
      window.removeEventListener('focus', onFocus);
    };
  }, [projectId, token]);

  return token ? count : 0;
}

function useTriageBadge(token: string | null, projectId: string): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    // Same pattern as useChatsBadge / useFeedbackBadge — derive the
    // signed-out / no-project zero in the return-value, defer initial pull
    // into a microtask.
    if (!token || !projectId) return;
    let cancelled = false;

    async function pull() {
      try {
        const data = await getTriageBadgeCount({ projectId });
        if (!cancelled) setCount(data.count ?? 0);
      } catch {
        if (!cancelled) setCount(0);
      }
    }

    queueMicrotask(pull);
    const handle = window.setInterval(pull, 60_000);
    const onFocus = () => pull();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
      window.removeEventListener('focus', onFocus);
    };
  }, [token, projectId]);

  return token && projectId ? count : 0;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, activeOrg, isOrgQa, logout } = useAuth();
  const { projectId } = useProjectFilter();
  const { enabled: codeReviewEnabled } = useCodeReviewEnabled();
  const [buildCommit, setBuildCommit] = useState('');
  const [buildMessage, setBuildMessage] = useState('');

  // Filter the static buildNavItems on the runtime feature flag. While the
  // flag is loading (enabled === null) we hide the entry — surfaces shouldn't
  // flicker into view and then disappear. Re-renders via the hook's tick
  // when the flag resolves (or when an admin toggles it).
  const visibleBuildNavItems = useMemo(
    () =>
      buildNavItems.filter((item) => item.path !== '/code-reviews' || codeReviewEnabled === true),
    [codeReviewEnabled],
  );

  // Unread chat count — drives the header Inbox button badge (and the desktop
  // notification side effects inside the hook). Also still surfaced on the
  // persistent chat dock.
  const chatsUnread = useChatsBadge(token, isOrgQa);
  const feedbackBadge = useFeedbackBadge(null, token);
  const triageBadge = useTriageBadge(token, projectId);
  const [chatsOpen, setChatsOpen] = useState(false);

  // Stamp localStorage when the user lands on /feedback so the "new since
  // last visit" counter resets to 0 until new items actually arrive. Per-page
  // project filter scopes this within the page itself.
  useEffect(() => {
    if (location.pathname.startsWith('/feedback')) {
      localStorage.setItem('feedback_last_viewed__all', new Date().toISOString());
    }
  }, [location.pathname]);

  useEffect(() => {
    apiPublic<{ commit?: string; commitMessage?: string }>(PATHS.health)
      .then((d) => {
        if (d.commit) setBuildCommit(d.commit);
        if (d.commitMessage) setBuildMessage(d.commitMessage);
      })
      .catch(() => {});
  }, []);

  // Admin-only org-wide presence — every authenticated dashboard tab emits
  // heartbeats while the user is interacting; the server only broadcasts the
  // live list to admins, so the cost on non-admin tabs is one tiny WS frame
  // per 30 s.
  useEffect(() => {
    if (!user) return;
    const ctl = startPresence();
    return () => ctl.stop();
  }, [user?.id]);

  const userInitials = user?.displayName
    ? user.displayName
        .split(/[\s._-]+/)
        .map((p) => p[0]?.toUpperCase() || '')
        .join('')
        .substring(0, 2)
    : user?.email?.substring(0, 2).toUpperCase() || '??';

  // Community = Feedback for everyone; QA also gets the support Inbox tile
  // (relocated here from the top header to save space). It carries the same
  // unread badge the header button used to show.
  const communityItems = useMemo<NavItem[]>(() => {
    const items = [...communityNavItems];
    if (isOrgQa) {
      items.push({ label: 'Inbox', path: '/chats', icon: MessageCircle, matchPaths: ['/chats'] });
    }
    return items;
  }, [isOrgQa]);

  // Sidebar nav, grouped by intent. Each group renders as a compact icon
  // grid (see NavTileGrid) so the whole nav fits without scrolling. Order
  // here IS the on-screen order; a separator is drawn between groups. Group
  // labels are intentionally not rendered — the separators alone delimit the
  // groups, which saves vertical space.
  const navGroups: Array<{
    label: string;
    items: NavItem[];
    getBadge?: (item: NavItem) => number;
  }> = [
    { label: 'Monitor', items: monitorNavItems },
    {
      label: 'Tasks',
      items: tasksNavItems,
      getBadge: (i) => (i.badge === 'triage' ? triageBadge : 0),
    },
    { label: 'Analytics', items: analyticsNavItems },
    {
      label: 'Community',
      items: communityItems,
      getBadge: (i) =>
        i.path === '/feedback' ? feedbackBadge : i.path === '/chats' ? chatsUnread : 0,
    },
    { label: 'Builds', items: visibleBuildNavItems },
    { label: 'Project Admin', items: projectAdminNavItems },
    { label: 'Testing', items: testingNavItems },
  ];

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground"
      >
        Skip to content
      </a>
      <TooltipProvider>
        <SidebarProvider defaultOpen={true} className="h-svh min-h-0 overflow-hidden">
          <Sidebar collapsible="icon">
            {/* ---- Header ---- */}
            <SidebarHeader className="px-3 py-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <SidebarTrigger className="shrink-0 h-7 w-7 overflow-hidden rounded-md bg-transparent p-0 flex items-center justify-center">
                  <img src="/logo-192.png" alt="Bugpunch" className="h-7 w-7 object-contain" />
                </SidebarTrigger>
                <span className="text-sm font-semibold truncate group-data-[collapsible=icon]:hidden">
                  <span className="text-foreground">Bug</span>
                  <span style={{ color: '#f97316' }}>punch</span>
                </span>
              </div>
              <ProjectFilter
                global
                pageKey="global"
                allowAll={false}
                className="group-data-[collapsible=icon]:hidden"
                triggerClassName="h-8 w-full text-xs"
              />
            </SidebarHeader>

            <SidebarContent>
              {navGroups.map((group, idx) => (
                <Fragment key={group.label}>
                  {idx > 0 && <SidebarSeparator />}
                  <SidebarGroup>
                    <NavTileGrid items={group.items} getBadge={group.getBadge} />
                  </SidebarGroup>
                </Fragment>
              ))}
            </SidebarContent>

            <SidebarSeparator />

            {/* ---- Footer ---- */}
            <SidebarFooter className="px-3 py-3">
              {/* User info */}
              <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="text-[10px] font-bold bg-primary/20 text-primary">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col min-w-0 group-data-[collapsible=icon]:hidden">
                  <span className="text-xs font-medium truncate">
                    {user?.displayName || user?.email || 'User'}
                  </span>
                  {activeOrg && (
                    <span className="text-[10px] text-muted-foreground truncate">
                      {activeOrg.name}
                    </span>
                  )}
                </div>
              </div>

              {/* Docs + Settings + Logout */}
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Documentation"
                    onClick={() => window.open('/docs', '_blank', 'noopener,noreferrer')}
                  >
                    <BookOpen />
                    <span>Docs</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Settings"
                    isActive={location.pathname === '/admin'}
                    onClick={() => navigate('/admin')}
                    className={cn(location.pathname === '/admin' && 'border-l-2 border-primary')}
                  >
                    <Settings />
                    <span>Settings</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Logout"
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                  >
                    <LogOut />
                    <span>Logout</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
              <div className="flex items-center gap-1.5 pt-1 group-data-[collapsible=icon]:hidden">
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('bp:open-bug-report'))}
                  title="Report a bug"
                  aria-label="Report a bug"
                  className="shrink-0 text-muted-foreground/60 transition-colors hover:text-destructive"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 2l1.88 1.88M14.12 3.88L16 2M9 7.13v-1a3.003 3.003 0 116 0v1M12 20c-3.3 0-6-2.7-6-6v-3a6 6 0 0112 0v3c0 3.3-2.7 6-6 6zM12 20v2M4.93 10H2M22 10h-2.93M7.1 16.2L4.8 18.4M16.9 16.2l2.3 2.2" />
                  </svg>
                </button>
                {buildCommit && (
                  <div
                    className="truncate text-[10px] text-muted-foreground/50"
                    title={buildMessage || buildCommit}
                  >
                    {buildCommit.substring(0, 7)}
                    {buildMessage ? ` — ${buildMessage}` : ''}
                  </div>
                )}
              </div>
            </SidebarFooter>
          </Sidebar>

          <SidebarInset className="h-svh min-h-0 overflow-hidden">
            <header className="z-30 flex h-12 shrink-0 items-center gap-3 border-b border-border bg-background/95 px-3 backdrop-blur">
              <SidebarTrigger className="h-8 w-8 md:hidden" />
              <div className="min-w-0 flex-1" />
              <GlobalTaskControls />
              <DownloadAppButton />
              <ChatMentionsInbox onOpenChats={() => setChatsOpen(true)} />
              <NotificationBell onOpenChats={() => setChatsOpen(true)} />
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('bp:open-help'))}
                className="rounded border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                title="Keyboard shortcuts"
                aria-label="Keyboard shortcuts"
              >
                ?
              </button>
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarFallback className="text-[10px] font-bold bg-primary/20 text-primary">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </header>
            <IngestHealthBanner />
            <StorageHealthBanner />
            <main id="main-content" className="min-h-0 flex-1 overflow-auto">
              {children}
            </main>
          </SidebarInset>
        </SidebarProvider>
        {/* Non-modal sheet: chat lives alongside the rest of the app rather
          than blocking it. `modal={false}` keeps the page interactive
          (no pointer trap, no scroll lock, no aria-hidden on main),
          `disablePointerDismissal` stops a stray click on the sidebar from
          closing the chat — QA closes via the X button or the chats nav
          item, never by accident. `hideOverlay` drops the dim + blur. */}
        <Sheet open={chatsOpen} onOpenChange={setChatsOpen} modal={false} disablePointerDismissal>
          <SheetContent
            side="right"
            hideOverlay
            className="!max-w-[min(960px,100vw)] w-[min(960px,100vw)] p-0 gap-0"
          >
            <Suspense fallback={<div className="h-full w-full bg-background" />}>
              <ChatThreadList crossProject />
            </Suspense>
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    </>
  );
}
